import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, BarChart3, Video, Megaphone, ChevronRight, Cpu, Award, TrendingUp, Layers } from 'lucide-react';
import api from '../lib/api';
import { useLanguage } from '../lib/i18n';

const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] } }) };

const platforms = ['Google Ads', 'Meta Ads', 'TikTok Ads', 'LinkedIn Ads', 'Snapchat Ads', 'Reddit Ads', 'Pinterest Ads', 'Telegram'];

const testimonials = [
  {
    name: 'AMX Custom Print',
    role: { en: 'Australia · Wholesale Apparel', ar: 'أستراليا · ملابس بالجملة' },
    text: {
      en: 'In a single day, we built their brand theme, logo, ad copy, and AI UGC video creatives, then launched the campaign. The result: 9 wholesale deals + 1 lead requesting 100 units.',
      ar: 'في يوم واحد، صممنا هوية العلامة والشعار ونصوص الإعلانات وإبداعات فيديو UGC بالذكاء الاصطناعي ثم أطلقنا الحملة. النتيجة: ٩ صفقات جملة + عميل محتمل طلب ١٠٠ وحدة.'
    },
    logo: '/clients/amx-logo.png',
    accent: '#F97316',
  },
  {
    name: 'AMTCO Ceramics',
    role: { en: 'Saudi Arabia · Premium Tiles & Construction', ar: 'السعودية · بلاط ومواد بناء' },
    text: {
      en: 'Full-stack launch: brand identity, social media, website, content production, and paid campaigns — delivered 200+ qualified leads in under a month.',
      ar: 'إطلاق متكامل: هوية بصرية، وسائل تواصل، موقع إلكتروني، إنتاج محتوى، وحملات إعلانية — حقّقنا أكثر من ٢٠٠ عميل محتمل مؤهل في أقل من شهر.'
    },
    logo: '/clients/amtco-logo.png',
    accent: '#D4A24A',
  },
  {
    name: 'Educational Institute',
    role: { en: 'Education · Enrollment Marketing', ar: 'تعليم · تسويق التسجيل' },
    text: {
      en: 'End-to-end marketing campaign for an education provider — drove 900+ enrollment leads through targeted creatives and high-performing paid funnels.',
      ar: 'حملة تسويق متكاملة لمؤسسة تعليمية — حقّقنا أكثر من ٩٠٠ عميل محتمل للتسجيل من خلال إبداعات مستهدفة وقمعات إعلانية عالية الأداء.'
    },
    logo: null,
    initials: 'EI',
    accent: '#06B6D4',
  },
];

function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(124,58,237,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,.4) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(124,58,237,0.12)_0%,transparent_70%)]" />
    </div>
  );
}

function FloatingOrbs() {
  return (
    <>
      <motion.div animate={{ y: [0, -40, 0], x: [0, 20, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-[#7C3AED]/10 blur-[100px]" />
      <motion.div animate={{ y: [0, 30, 0], x: [0, -15, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }} className="absolute bottom-1/3 left-[20%] w-48 h-48 rounded-full bg-[#06B6D4]/10 blur-[80px]" />
      <motion.div animate={{ y: [0, -20, 0], opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/3 right-[15%] w-2 h-2 rounded-full bg-[#7C3AED]" />
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: 'linear' }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-[#7C3AED]/[0.06]" />
    </>
  );
}

export default function Home() {
  const [caseStudies, setCaseStudies] = useState([]);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -80]);
  const { t, lang, isRTL } = useLanguage();

  useEffect(() => { api.getCaseStudies().then(res => setCaseStudies(res.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <div data-testid="home-page" className="bg-[#050508]">
      <HeroSection heroY={heroY} t={t} isRTL={isRTL} />
      <ServicesSection t={t} />
      <PlatformsMarquee t={t} isRTL={isRTL} />
      <WhyUsSection t={t} isRTL={isRTL} />
      <TestimonialsSection t={t} lang={lang} />
      <CTASection t={t} />
    </div>
  );
}

function HeroSection({ heroY, t, isRTL }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <GridBackground />
      <FloatingOrbs />
      <motion.div style={{ y: heroY }} className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-28 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-4xl">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="mb-4 sm:mb-6">
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/20 text-[#A855F7] text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em]">
              <Cpu className="w-3 sm:w-3.5 h-3 sm:h-3.5" /> {t('home.badge')}
            </span>
          </motion.div>
          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1} className="font-poppins font-black text-3xl sm:text-5xl lg:text-7xl text-white leading-[1.1] tracking-tighter mb-4 sm:mb-6">
            {t('home.title1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] via-[#A855F7] to-[#06B6D4]">{t('home.titleHighlight')}</span>
            {t('home.title2') && <> {t('home.title2')}</>}
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={2} className="text-[#A1A1AA] text-sm sm:text-lg md:text-xl max-w-2xl leading-relaxed mb-6 sm:mb-10">{t('home.subtitle')}</motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to="/contact" data-testid="hero-cta-consultation" className="group px-6 sm:px-8 py-3 sm:py-4 bg-[#7C3AED] text-white font-semibold rounded-full hover:bg-[#6D28D9] hover:shadow-[0_8px_30px_rgba(124,58,237,0.4)] transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base">
              {t('home.ctaPrimary')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/portfolio" data-testid="hero-cta-portfolio" className="px-6 sm:px-8 py-3 sm:py-4 text-white font-semibold rounded-full border border-white/15 hover:border-[#7C3AED]/50 hover:bg-[#7C3AED]/5 transition-all duration-300 text-center text-sm sm:text-base">
              {t('home.ctaSecondary')}
            </Link>
          </motion.div>
        </div>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5} className="mt-12 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {[
            { num: '3+', label: t('home.stat1'), icon: Award },
            { num: '200%', label: t('home.stat2'), icon: TrendingUp },
            { num: '1M+', label: t('home.stat3'), icon: BarChart3 },
            { num: '5+', label: t('home.stat4'), icon: Layers },
          ].map((stat) => (
            <motion.div key={stat.num} whileHover={{ y: -4, borderColor: 'rgba(124,58,237,0.3)' }} className="p-4 sm:p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] backdrop-blur-sm transition-all group">
              <stat.icon className="w-4 sm:w-5 h-4 sm:h-5 text-[#7C3AED] mb-2 sm:mb-3" />
              <div className="font-poppins font-bold text-xl sm:text-3xl text-white mb-1">{stat.num}</div>
              <div className="text-[#71717A] text-xs sm:text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

function ServicesSection({ t }) {
  const services = [
    { icon: Sparkles, title: t('home.svc1'), desc: t('home.svc1Desc') },
    { icon: Video, title: t('home.svc2'), desc: t('home.svc2Desc') },
    { icon: Megaphone, title: t('home.svc3'), desc: t('home.svc3Desc') },
  ];
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#0A0A12] relative" data-testid="services-section">
      <GridBackground />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{t('home.servicesTag')}</span>
          <h2 className="font-poppins font-bold text-2xl sm:text-4xl md:text-5xl text-white tracking-tight">{t('home.servicesTitle')}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {services.map((svc, i) => (
            <motion.div key={svc.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} whileHover={{ y: -6, borderColor: 'rgba(124,58,237,0.2)' }}
              className="group p-5 sm:p-7 bg-white/[0.02] rounded-2xl border border-white/[0.06] transition-all duration-300 cursor-pointer hover:bg-white/[0.04]" data-testid={`service-card-${i}`}>
              <div className="w-10 sm:w-11 h-10 sm:h-11 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center mb-4 sm:mb-5"><svc.icon className="w-5 h-5 text-[#7C3AED]" /></div>
              <h3 className="font-poppins font-semibold text-base sm:text-lg text-white mb-2">{svc.title}</h3>
              <p className="text-[#71717A] text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">{svc.desc}</p>
              <Link to="/services" className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#7C3AED]">{t('home.learnMore')} <ChevronRight className="w-4 h-4" /></Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlatformsMarquee({ t, isRTL }) {
  return (
    <section className="py-10 sm:py-16 bg-[#050508] border-y border-white/[0.04]" data-testid="platforms-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mb-6 sm:mb-10 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#71717A]">{t('home.platformsTag')}</span>
      </div>
      <div className="relative overflow-hidden">
        <motion.div animate={{ x: isRTL ? ['0%', '50%'] : ['0%', '-50%'] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }} className="flex gap-4 sm:gap-8 whitespace-nowrap">
          {[...platforms, ...platforms].map((p, i) => (
            <div key={`${p}-${i}`} className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full bg-white/[0.03] border border-white/[0.06]">
              <BarChart3 className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-[#7C3AED]" />
              <span className="text-white/60 font-medium text-xs sm:text-sm">{p}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function WhyUsSection({ t, isRTL }) {
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#0A0A12] relative" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: isRTL ? 40 : -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{t('home.whyTag')}</span>
            <h2 className="font-poppins font-bold text-2xl sm:text-4xl md:text-5xl text-white tracking-tight mb-4 sm:mb-6">{t('home.whyTitle')}</h2>
            <p className="text-[#A1A1AA] text-sm sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8">{t('home.whySubtitle')}</p>
            <div className="space-y-3 sm:space-y-4">
              {t('home.whyPoints').map((item) => (
                <motion.div key={item} initial={{ opacity: 0, x: isRTL ? 20 : -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0"><div className="w-2 h-2 rounded-full bg-[#7C3AED]" /></div>
                  <span className="text-white/80 font-medium text-sm sm:text-base">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: isRTL ? -40 : 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/[0.06]">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800" alt="Dashboard" className="w-full h-[280px] sm:h-[400px] object-cover" />
            </div>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} className="absolute -bottom-4 sm:-bottom-6 -left-2 sm:-left-6 p-4 sm:p-5 rounded-2xl bg-[#12121C] border border-[#7C3AED]/20 shadow-[0_20px_40px_rgba(0,0,0,0.5)]">
              <div className="text-[#7C3AED] font-poppins font-bold text-xl sm:text-2xl">+200%</div>
              <div className="text-[#71717A] text-xs sm:text-sm">{t('home.stat2')}</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ t, lang }) {
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#0A0A12] relative" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10 sm:mb-16">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#06B6D4] mb-4 block">{t('home.testimonialsTag')}</span>
          <h2 className="font-poppins font-bold text-2xl sm:text-4xl md:text-5xl text-white tracking-tight">{t('home.testimonialsTitle')}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {testimonials.map((tm) => (
            <motion.div key={tm.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} whileHover={{ y: -4 }}
              className="flex flex-col p-5 sm:p-7 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-[#7C3AED]/25 transition-all duration-300" data-testid={`testimonial-${tm.name.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-black/40 border border-white/[0.06] flex items-center justify-center mb-4 sm:mb-5 flex-shrink-0">
                {tm.logo ? (
                  <img src={tm.logo} alt={`${tm.name} logo`} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-poppins font-bold text-base sm:text-lg" style={{ color: tm.accent }}>{tm.initials}</span>
                )}
              </div>
              <p className="text-[#D4D4D8] text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6 flex-1">"{tm.text[lang]}"</p>
              <div className="pt-3 sm:pt-4 border-t border-white/[0.06]">
                <div className="font-poppins font-semibold text-sm text-white">{tm.name}</div>
                <div className="text-[11px] sm:text-xs text-[#71717A] mt-0.5">{tm.role[lang]}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection({ t }) {
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-[#050508] relative overflow-hidden" data-testid="cta-section">
      <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.05, 0.12, 0.05] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] rounded-full bg-[#7C3AED] blur-[150px]" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 md:px-12 text-center">
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-poppins font-bold text-2xl sm:text-4xl md:text-5xl text-white tracking-tight mb-4 sm:mb-6">{t('home.ctaTitle')}</motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-[#A1A1AA] text-sm sm:text-lg mb-6 sm:mb-10 max-w-xl mx-auto">{t('home.ctaSubtitle')}</motion.p>
        <Link to="/contact" data-testid="cta-contact-link" className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-4 bg-[#7C3AED] text-white font-semibold rounded-full hover:bg-[#6D28D9] hover:shadow-[0_8px_30px_rgba(124,58,237,0.4)] transition-all duration-300 text-base sm:text-lg">
          {t('home.ctaBtn')} <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  );
}
